import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { MenuOptionEditBoxProps } from "components/menu/ui/MenuSectionEditBox";
import { Box, Stack, Tooltip } from "@mui/material";
import MenuItemEditBox from "components/menu/ui/MenuItemEditBox";
import styled from "styled-components";
import DeleteIcon from "@mui/icons-material/Delete";
import CircleIconButton, {
  CircleIcon,
} from "components/common/ui/CircleIconButton";
import PillButton from "components/common/ui/PillButton";
import {
  DisplayMenuItem,
  DisplayMenuSection,
} from "models/menu/menuContentsReducer";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import {
  combineUUIDs,
  getLastUUIDFromPath,
  getMenuOptionByPath,
  removeLastUUIDFromPath,
} from "utils/menu/menu/menuContentDisplay";
import { isDisplayMenuItem } from "models/menu/menu";
import { SpacedAutocompleteInput } from "components/common/ui/SpacedTextInput";
import { SIDE_OPTION_GROUP_NAME_OPTIONS } from "models/menu/constants/sectionName";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import useMenuOptionActions from "hooks/menu/useMenuOptionActions";

const BorderBox = styled(Box)`
  border: 0.1rem solid #eee;
  padding: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
`;

const MenuOptionGroupEditBox: FC<MenuOptionEditBoxProps> = ({
  displayMenuOptionUUIDPath,
  menuContents,
  dispatchMenuContents,
  foodItemOptions,
}) => {
  const displayUUID: string = getLastUUIDFromPath(displayMenuOptionUUIDPath);
  const [parentMenuOption, setParentMenuOption] = useState<
    DisplayMenuSection | DisplayMenuItem
  >();
  const [optionGroupName, setOptionGroupName] = useState<string>("");
  const { handleMove, handleRemove, handleAdd, handleUpdate } =
    useMenuOptionActions(
      menuContents,
      dispatchMenuContents,
      displayMenuOptionUUIDPath,
    );

  useEffect(() => {
    const parentUUID = removeLastUUIDFromPath(displayMenuOptionUUIDPath);
    if (!parentUUID) {
      return;
    }
    const potentialParent = getMenuOptionByPath(menuContents, parentUUID);
    if (potentialParent) {
      setParentMenuOption(potentialParent);
    }

    const optionGroup = getMenuOptionByPath(
      menuContents,
      displayMenuOptionUUIDPath,
    );
    if (optionGroup && !isDisplayMenuItem(optionGroup)) {
      setOptionGroupName(optionGroup.name);
    }
  }, [displayMenuOptionUUIDPath, menuContents]);

  /**
   * Get the index of the menu option group
   */
  const getMenuOptionGroupIndex = useCallback(() => {
    if (!parentMenuOption) {
      return undefined;
    }

    return isDisplayMenuItem(parentMenuOption)
      ? Object.keys(parentMenuOption.side_option_groups).findIndex(
          (key) => key === displayUUID,
        ) + 1
      : Object.keys(parentMenuOption.options).findIndex(
          (key) => key === displayUUID,
        ) + 1;
  }, [displayUUID, parentMenuOption]);

  /**
   * Update the menu option group
   */
  const handleUpdateMenuOptionGroup = useCallback(() => {
    handleUpdate({
      name: optionGroupName,
    });
  }, [handleUpdate, optionGroupName]);

  /**
   * Handle change menu option group name
   */
  const handleChangeMenuOptionGroupName = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      if (!newValue) {
        setOptionGroupName("");
      } else {
        setOptionGroupName(newValue);
      }
    },
    [],
  );

  return (
    <Box display="flex">
      <Box display="flex">
        <Tooltip title="Side option group">
          <CircleIcon natural sizepx={30}>
            <SubdirectoryArrowRightIcon />
          </CircleIcon>
        </Tooltip>
      </Box>
      <BorderBox>
        <Box display="flex" paddingBottom={1}>
          <Box display="flex" padding={1}>
            <Box>
              <Tooltip title="Move this group up">
                <SoftSquareIconButton onClick={handleMove("up")}>
                  <KeyboardArrowUpIcon />
                </SoftSquareIconButton>
              </Tooltip>
            </Box>
            <Box paddingLeft={1} paddingRight={1}>
              <Tooltip title="Move this group down">
                <SoftSquareIconButton onClick={handleMove("down")}>
                  <KeyboardArrowDownIcon />
                </SoftSquareIconButton>
              </Tooltip>
            </Box>
            <SpacedAutocompleteInput
              label={`Group ${getMenuOptionGroupIndex() ?? ""} Name`}
              value={optionGroupName ?? ""}
              onChange={handleChangeMenuOptionGroupName}
              onBlur={handleUpdateMenuOptionGroup}
              options={SIDE_OPTION_GROUP_NAME_OPTIONS}
              size="small"
              shrink
            />
          </Box>
          <Box padding={1}>
            <Tooltip title="Remove this group and it's options">
              <CircleIconButton positive danger onClick={handleRemove}>
                <DeleteIcon />
              </CircleIconButton>
            </Tooltip>
          </Box>
        </Box>
        <Stack spacing={1}>
          {Object.keys(
            (
              getMenuOptionByPath(
                menuContents,
                displayMenuOptionUUIDPath,
              ) as DisplayMenuSection
            ).options,
          ).map((itemSideUUID, itemSideIdx) => (
            <MenuItemEditBox
              key={itemSideUUID}
              displayMenuOptionUUIDPath={combineUUIDs([
                displayMenuOptionUUIDPath,
                itemSideUUID,
              ])}
              menuContents={menuContents}
              dispatchMenuContents={dispatchMenuContents}
              label={`Option ${itemSideIdx + 1}`}
              foodItemOptions={foodItemOptions}
            />
          ))}
        </Stack>
        <PillButton text="Add option" onClick={handleAdd("menu item")} />
      </BorderBox>
    </Box>
  );
};

export default MenuOptionGroupEditBox;
